<script setup lang="ts">
const router = useRouter();
const route = useRoute();

useHead({
  title: 'How to integrate Mixpanel with ZapScale?',
  meta: [
    {
      name: 'description',
      content:
        'When you integrate Mixpanel with ZapScale, you gain valuable insights into how customers interact with your product and their overall digital experience.',
    },
    {
      property: 'og:description',
      content:
        'When you integrate Mixpanel with ZapScale, you gain valuable insights into how customers interact with your product and their overall digital experience.',
    },
    {
      property: 'og:title',
      content: 'How to integrate Mixpanel with ZapScale?',
    },
    {
      property: 'og:url',
      content: 'https://www.zapscale.com' + route.path,
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.zapscale.com' + route.path,
    },
  ],
});

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ZapScale',
  description:
    'A single-source customer success management SaaS platform with 50 pre-built playbooks, 40 out-of-the-box KPIs, and unlimited user logins.',
  url: 'https://www.zapscale.com/',
  logo: 'https://www.zapscale.com/images/Header_logo.svg',
  sameAs: [
    'https://www.facebook.com/zapscale',
    'https://www.linkedin.com/company/zapscale/',
    'https://twitter.com/ZapScale',
  ],
}));

const tabList = [
  'All',
  'CRM',
  'Ticketing and Features',
  'Subscription and Payments',
  'Communication',
  'Product Analytics',
  'Data Warehouse',
];

const selectedTab = ref(0);

function onTabClick(index: number) {
  selectedTab.value = index;
  switch (index) {
    case 0:
      router.push({ path: '/integrations', hash: '' });
      break;
    case 1:
      router.push({ path: '/integrations', hash: '#crm' });
      break;
    case 2:
      router.push({ path: '/integrations', hash: '#ticketing-and-features' });
      break;
    case 3:
      router.push({ path: '/integrations', hash: '#subscription-and-payments' });
      break;
    case 4:
      router.push({ path: '/integrations', hash: '#communication' });
      break;
    case 5:
      router.push({ path: '/integrations', hash: '#product-analytics' });
      break;
    case 6:
      router.push({ path: '/integrations', hash: '#data-warehouse' });
      break;
    default:
      break;
  }
}
</script>

<template>
  <div class="bg-[#EFF7FD]">
    <div class="container mx-auto">
      <div class="flex flex-col gap-y-3 px-5 py-6 md:gap-y-12 lg:py-12">
        <div
          class="no-scrollbar mx-auto flex h-10 w-full flex-row items-center rounded-full bg-white px-4 max-md:overflow-x-scroll max-md:whitespace-nowrap md:h-[51px] md:w-fit md:justify-center"
        >
          <div
            v-for="(tab, index) in tabList"
            :key="tab"
            :id="index.toString()"
            class="relative flex h-full cursor-pointer items-center"
            @click="onTabClick(index)"
          >
            <p
              class="px-4 leading-[19.81px] text-[#0070C0] md:text-xs lg:text-[17px]"
              :class="[
                index !== tabList.length - 1 && 'border-r-[1px] border-r-[#0070C0]',
                index === selectedTab && 'font-bold',
              ]"
            >
              {{ tab }}
            </p>
            <div
              v-if="index === selectedTab"
              class="absolute bottom-0 left-4 right-4 mx-auto h-[1px] min-w-max border-b-[1px] border-b-[#0070C0]"
            ></div>
          </div>
        </div>
        <div class="mx-auto flex w-[90%] flex-col gap-y-3 text-left md:w-4/5 md:gap-y-4">
          <h1 class="font-merriweather text-2xl font-extrabold text-[#0070C0] md:text-[25px]">
            ZapScale’s Integration with Mixpanel
          </h1>
          <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
            When you integrate Mixpanel, a powerful analytics platform, with ZapScale, you gain valuable insights into
            how customers interact with your product and their overall digital experience. This data helps your CS team
            understand customer behavior, identify potential roadblocks, and ultimately drive product adoption and
            success.
          </p>
          <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
            Here's a breakdown of the key information ZapScale can typically gather from Mixpanel:
          </p>
          <div>
            <span class="text-base font-bold text-[#3A3A3C] md:text-xl">Customer and user information:</span>
            <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
              ZapScale can pull all your customer information, such as customer name, customer ID, email ID, domain and
              other customer-related information.
            </p>
          </div>
          <div>
            <span class="text-base font-bold text-[#3A3A3C] md:text-xl">User information:</span>
            <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
              ZapScale pulls user information such as username, their respective customer name, user ID, user email,
              user role, user role ID and other user related information.
            </p>
          </div>
          <ul class="list-disc space-y-4 pl-4 text-base font-normal text-[#3A3A3C] md:pl-6 md:text-xl">
            <li>
              <span class="font-semibold">Limited Customer Data:</span>
              <ul class="list-disc space-y-3 pl-6 pt-4 md:pl-12">
                <li>
                  <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
                    While Mixpanel focuses on user behavior, it might store basic contact details associated with user
                    profiles, like names and email addresses (if provided by your application).
                  </p>
                </li>
              </ul>
            </li>
            <li>
              <span class="font-semibold">Engagement Data:</span>
              <ul class="list-disc space-y-3 pl-6 pt-4 md:pl-12">
                <li>
                  <span class="font-semibold">User Activity Data:</span> Mixpanel excels at capturing various user
                  activities within your application, providing valuable insights into product usage and engagement.
                  This might include:
                  <ul class="list-disc space-y-3 pl-4 pt-4 md:pl-8">
                    <li>
                      <span class="font-semibold">Feature Usage:</span> Track which features users access most
                      frequently, helping identify areas of high value and potential areas for improvement.
                    </li>
                    <li>
                      <span class="font-semibold">User Journeys:</span> Analyze user paths within your application to
                      understand how users navigate and interact with different functionalities. This can help identify
                      bottlenecks or confusing user experiences.
                    </li>
                    <li>
                      <span class="font-semibold">Time Spent on Features:</span> Track how much time users spend on
                      specific features to understand engagement levels and identify areas where users might be
                      struggling.
                    </li>
                  </ul>
                </li>
                <li>
                  <span class="font-semibold">Event Tracking:</span> Mixpanel allows tracking custom events triggered by
                  user actions within your application. This provides rich data for your CS team, including:
                  <ul class="list-disc pl-4 pt-4 md:pl-8">
                    <li>
                      <span class="font-semibold">Product Adoption Events:</span> Track events that indicate successful
                      product adoption, such as completing a key task or achieving a specific goal within the
                      application.
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <div class="mx-auto flex w-4/5 flex-col gap-y-3 text-left md:gap-y-4">
          <h2 class="font-merriweather text-2xl font-extrabold text-[#0070C0] md:text-[25px]">
            Benefits of Mixpanel Integration with ZapScale
          </h2>
          <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
            Mixpanel integration offers a wealth of user behavior data for your CS team, enabling them to:
          </p>
          <ul class="list-disc space-y-3 pl-4 text-base font-normal text-[#3A3A3C] md:pl-6 md:text-xl">
            <li>
              <span class="font-semibold">Personalize Customer Journeys:</span> Analyze user activity data to tailor
              onboarding experiences, product tours, and support interactions to individual customer needs.
            </li>
            <li>
              <span class="font-semibold">Proactive Customer Engagement:</span> Identify at-risk customers based on
              inactivity or lack of engagement with core features. This allows for proactive outreach to address
              concerns and prevent churn.
            </li>
            <li>
              <span class="font-semibold">Improved User Education:</span> Analyze user journeys and feature usage to
              identify areas where users might require additional guidance. This can inform the creation of targeted
              content or self-service resources.
            </li>
            <li>
              <span class="font-semibold">Measure Product Adoption & Success:</span> Track user activity data and
              engagement metrics to measure product adoption and understand how effectively your product is meeting
              customer needs.
            </li>
            <li>
              <span class="font-semibold">Identify Feature Usage Trends:</span> Analyze feature usage data to identify
              which features are most valuable to customers and prioritize future product development efforts
              accordingly.
            </li>
          </ul>
          <p class="text-base font-normal text-[#3A3A3C] md:text-xl">
            Overall, a Mixpanel integration empowers your CS team with a deeper understanding of customer behavior
            within your product. This data is crucial for improving product adoption, driving customer success, and
            ultimately achieving long-term customer retention.
          </p>
        </div>
        <div
          class="no-scrollbar mx-auto flex h-10 w-full flex-row items-center rounded-full bg-white px-4 max-md:overflow-x-scroll max-md:whitespace-nowrap md:h-[51px] md:w-fit md:justify-center"
        >
          <div
            v-for="(tab, index) in tabList"
            :key="tab"
            :id="index.toString()"
            class="relative flex h-full cursor-pointer items-center"
            @click="onTabClick(index)"
          >
            <p
              class="px-4 leading-[19.81px] text-[#0070C0] md:text-xs lg:text-[17px]"
              :class="[
                index !== tabList.length - 1 && 'border-r-[1px] border-r-[#0070C0]',
                index === selectedTab && 'font-bold',
              ]"
            >
              {{ tab }}
            </p>
            <div
              v-if="index === selectedTab"
              class="absolute bottom-0 left-4 right-4 mx-auto h-[1px] min-w-max border-b-[1px] border-b-[#0070C0]"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="bg-[#FFC52F]">
    <div class="container mx-auto">
      <div class="px-5 py-10 md:px-14">
        <span
          class="mx-auto mb-6 block text-center font-merriweather text-2xl font-bold text-black md:mb-8 xl:w-[971px] xl:text-3xl xl:leading-[50px]"
        >
          Want to know more about how ZapScale seamless integrates<br class="hidden lg:block" />
          with your favorite tool?
        </span>
        <NuxtLink
          to="/demo/#book-a-demo"
          class="mx-auto block h-[41px] w-[179px] rounded-full bg-[#FF395B] px-6 py-2 text-base font-semibold text-white hover:border hover:border-[#FF395B] hover:bg-white hover:text-[#FF395B]"
        >
          <span class="flex items-center justify-between">
            Talk to us
            <Icon name="solar:arrow-right-linear" />
          </span>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
